import {Component, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {BoulderListService} from '../../services/boulder-list.service';
import {BoulderList} from '../../model/boulder-list';
import {BoulderService} from '../../services/boulder.service';
import {BoulderListEntry} from '../../model/boulder-list-entry';
import {AuthService} from '../../services/auth.service';
import {AlertController, ItemReorderEventDetail, ModalController, NavController} from '@ionic/angular';
import {TranslateService} from '@ngx-translate/core';
import {BoulderDetailsPage} from '../boulder-details/boulder-details.page';
import {firstValueFrom} from 'rxjs';
import {GymService} from '../../services/gym.service';
import {RankingList} from '../../components/ranking-list/ranking-list.component';
import {Boulder} from '../../model/boulder';

@Component({
  selector: 'app-boulder-list-details',
  templateUrl: './boulder-list-details.page.html',
  styleUrls: ['./boulder-list-details.page.scss'],
})
export class BoulderListDetailsPage implements OnInit {

  isLoading = false;
  boulderList: BoulderList;
  boulders: Boulder[] = [];
  isOwnList = true;
  publicList: boolean = false;

  constructor(
    private translateService: TranslateService,
    private gymService: GymService,
    private modalCtrl: ModalController,
    private router: NavController,
    private alertCtrl: AlertController,
    private boulderService: BoulderService,
    private authService: AuthService,
    private activatedRoute: ActivatedRoute,
    private translate: TranslateService,
    private boulderListService: BoulderListService) {

    this.activatedRoute.params.subscribe(async params => {
      if (params['listId']) {
        await this.reloadList(params['listId']);
      }
    });
  }

  ngOnInit() {
  }

  private async reloadList(listId: string) {
    this.isLoading = true;
    try {
      const boulderList = await firstValueFrom(this.boulderListService.getBoulderList$(listId));
      if (boulderList) {
        this.isOwnList = this.authService.getCurrentUsername() == boulderList.author;
        this.publicList = boulderList.publicList;
        this.boulders = (await firstValueFrom(this.boulderService.getBoulders$())).filter(
          b => boulderList.boulderIds.indexOf(b.boulderId) > -1);
        this.boulders.sort((b1, b2) =>
          boulderList.boulderIds.indexOf(b1.boulderId) - boulderList.boulderIds.indexOf(b2.boulderId));
        this.boulderList = boulderList;
      }
    } finally {
      this.isLoading = false;
    }
  }

  async removeList() {
    const alert = await this.alertCtrl.create({
      header: this.translate.instant('boulder-list-details-page.delete-header'),
      buttons: [
        {
          text: this.translate.instant('boulder-list-details-page.cancel'),
          role: 'cancel',
          cssClass: 'secondary'
        },
        {
          text: this.translate.instant('boulder-list-details-page.ok'),
          handler: async () => {
            await this.boulderListService.removeBoulderList(this.boulderList.listId);
            await this.router.navigateRoot('/tabs/boulder-lists/sync');
          }
        }],
    });
    await alert.present();
  }

  async showBoulder(boulder: BoulderListEntry) {
    const modal = await this.modalCtrl.create({
      id: 'boulder-details-modal',
      component: BoulderDetailsPage,
      componentProps: {boulderId: boulder.boulderId, boulderIds: this.boulderList.boulderIds}
    });
    return await modal.present();
  }

  async publicToggleChanged() {
    await this.boulderListService.changeVisibility(this.boulderList.listId, this.publicList);
    this.gymService.syncActiveGym();
  }

  start4x4() {
    this.router.navigateForward('/4x4/' + this.boulderList.listId);
  }

  async doReorder(event: Event) {
    if (!this.isOwnList) {
      return;
    }
    this.boulders = (event as CustomEvent<ItemReorderEventDetail>).detail.complete(this.boulders);
    await this.boulderListService.updateBoulderIds(this.boulderList.listId, this.boulders.map(b => b.boulderId));
  }

  async renameList() {
    const alert = await this.alertCtrl.create({
      header: this.translateService.instant('boulder-list-details-page.rename'),
      inputs: [
        {
          name: 'name',
          type: 'text',
          placeholder: this.boulderList.name,
          attributes: {
            minLength: 3,
            maxLength: 100
          }
        }
      ],
      buttons: [
        {
          text: this.translateService.instant('shared.cancel'),
          role: 'cancel'
        }, {
          text: this.translateService.instant('shared.ok'),
          handler: async (data) => {
            if (data?.name?.length > 2) {
              this.renameListAndReload(data.name);
            }
          }
        }
      ]
    });

    await alert.present();
  }

  private async renameListAndReload(newName: string) {
    this.isLoading = true;
    try {
      await this.boulderListService.updateNameOfList(this.boulderList.listId, newName);
    } finally {
      this.isLoading = false;
    }
    this.reloadList(this.boulderList.listId);
  }

  async showRanking() {
    const modal = await this.modalCtrl.create({
      component: RankingList,
      componentProps: {
        boulders: this.boulders,
        showHeader: true
      }
    });
    return await modal.present();
  }

  async removeBoulderFromList(boulderId: string) {
    this.isLoading = true;
    try {
      await this.boulderListService.removeBoulderIdFromList(boulderId, this.boulderList.listId);
      this.reloadList(this.boulderList.listId);
    } finally {
      this.isLoading = false;
    }
  }
}
